import React from 'react';
import { Link } from 'react-router-dom';
import { translations } from '../translations';
import DriviLogo from '../assets/logo.svg';
import { 
  Facebook, 
  Twitter, 
  Linkedin, 
  Instagram, 
  Mail, 
  Phone, 
  MapPin,
  ChevronRight,
  Shield,
  Globe
} from 'lucide-react';

function Footer({ language = 'en' }) {
  const t = translations[language].footer;

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Handle newsletter subscription
  };

  return (
    <footer className="bg-[#2C3E50] text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          {/* Company Info - 2 columns */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-6">
              <img src={DriviLogo} alt="Drivi" className="h-8 w-auto" />
              <span className="text-xl font-bold">Drivi</span>
            </div>
            <p className="text-gray-400 mb-6">
              Enterprise-grade fleet management platform for real-time tracking, maintenance, and optimization.
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors">
                <Mail className="h-5 w-5" />
                <a href="mailto:contact@drivi.com">contact@drivi.com</a>
              </div>
              <div className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors">
                <Phone className="h-5 w-5" />
                <a href="tel:+34 676 984 400">+34 676 984 400</a>
              </div>
              <div className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors">
                <MapPin className="h-5 w-5" />
                <span>07011 C/Asegra, C. Can Valero, 17, A</span>
              </div>
            </div>
            {/* Powered by THE 404 studio */}
            <div className="flex flex-col items-start mt-8">
              <a
                href="https://the404.studio/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-start"
                aria-label="THE 404 studio website"
              >
                <img
                  src="/assets/logo-the404-studio-white-desarrollo-web.png"
                  alt="THE 404 studio logo"
                  width={80}
                  height={20}
                  className="grayscale group-hover:grayscale-0 transition-all duration-300 mb-2"
                  style={{ minWidth: 90 }}
                />
                <span className="text-sm text-gray-400 group-hover:text-primary font-mono" style={{ lineHeight: 1.2 }}>
                  {t.poweredBy404}
                </span>
              </a>
            </div>
          </div>

          {/* Product & Services - 1 column */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-6">{t.sections.product.title}</h3>
            <ul className="space-y-3">
              {t.sections.product.links.map((link, index) => (
                <li key={index}>
                  <Link to="/" className="text-gray-400 hover:text-white transition-colors flex items-center">
                    <ChevronRight className="h-4 w-4 mr-2" />
                    {link}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company - 1 column */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-6">{t.sections.company.title}</h3>
            <ul className="space-y-3">
              {t.sections.company.links.map((link, index) => (
                <li key={index}>
                  <Link to="/" className="text-gray-400 hover:text-white transition-colors flex items-center">
                    <ChevronRight className="h-4 w-4 mr-2" />
                    {link}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal - 1 column */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-6">{t.sections.legal.title}</h3>
            <ul className="space-y-3">
              {t.sections.legal.links.map((link, index) => (
                <li key={index}>
                  <Link to="/" className="text-gray-400 hover:text-white transition-colors flex items-center">
                    <ChevronRight className="h-4 w-4 mr-2" />
                    {link}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter - 1 column */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-6">Stay Updated</h3>
            <form onSubmit={handleNewsletterSubmit} className="space-y-4">
              <p className="text-gray-400">Subscribe to our newsletter for updates and insights.</p>
              <div className="relative">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="flex flex-wrap justify-center gap-6 py-8 border-t border-gray-700">
          <a 
            href="https://x.com/DriviApp" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Follow us on X (Twitter)"
          >
            <Twitter className="h-6 w-6" />
          </a>
          <a 
            href="https://www.linkedin.com/company/drivitrack/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Follow us on LinkedIn"
          >
            <Linkedin className="h-6 w-6" />
          </a>
          <a 
            href="https://www.instagram.com/drivi.app" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Follow us on Instagram"
          >
            <Instagram className="h-6 w-6" />
          </a>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700 bg-[#243342]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-8 gap-y-2 text-sm text-gray-400">
              <span>{t.copyright}</span>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/security" className="hover:text-white transition-colors">Security</Link>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-gray-400">
                <Shield className="h-5 w-5" />
                <span className="text-sm">SOC 2 Type II Certified</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <Globe className="h-5 w-5" />
                <select 
                  className="bg-transparent text-sm focus:outline-none cursor-pointer"
                  value={language}
                  onChange={(e) => {
                    // Handle language change
                  }}
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer; 
 